import { useId, useState } from "react";
import { FileText, ImageOff, Mail, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ShareEmailOverview, ShareEmailOverviewItem } from "@shared/share-email-overview";

export interface ShareEmailOverviewEditorProps {
  overview?: ShareEmailOverview;
  excludedIds: number[];
  onExcludedIdsChange: (ids: number[]) => void;
  mode: "wait" | "without";
  onModeChange: (mode: "wait" | "without") => void;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Thumbnail({ src }: { src?: string }) {
  const [failed, setFailed] = useState(false);
  // Only the server's tiny embedded JPEG is allowed; never request an original.
  const safe = src && /^data:image\/jpeg;base64,[A-Za-z0-9+/]+={0,2}$/.test(src);
  return (
    <div className="flex h-11 w-14 shrink-0 items-center justify-center overflow-hidden rounded border border-border bg-muted text-muted-foreground">
      {safe && !failed ? (
        <img src={src} alt="" width={56} height={44} className="h-full w-full object-contain" onError={() => setFailed(true)} />
      ) : <FileText className="h-4 w-4" aria-hidden="true" />}
    </div>
  );
}

function thumbnailLabel(item: ShareEmailOverviewItem, mode: "wait" | "without"): string {
  if (item.thumbnailStatus === "budget") return "Text only · thumbnail limit";
  if (item.thumbnailStatus === "unavailable") return "Text only · thumbnail unavailable";
  if (mode === "without") return "Text only · thumbnails off";
  if (item.thumbnailStatus === "pending") return "Thumbnail preparing";
  return item.thumbnailDataUrl ? "Thumbnail ready" : "Text only · thumbnail unavailable";
}

/** Presentation only: exclusions affect this email, never the underlying share. */
export function ShareEmailOverviewEditor({
  overview, excludedIds, onExcludedIdsChange, mode, onModeChange, loading, error, onRetry,
}: ShareEmailOverviewEditorProps) {
  const headingId = useId();
  const items = overview?.items ?? [];
  const excluded = new Set(excludedIds);
  const included = items.filter(item => !excluded.has(item.id));
  const pendingCount = included.filter(item => item.thumbnailStatus === "pending").length;
  const removedCount = items.length - included.length;
  const toggle = (id: number) => onExcludedIdsChange(
    excluded.has(id) ? excludedIds.filter(value => value !== id) : [...new Set([...excludedIds, id])],
  );

  return (
    <section aria-labelledby={headingId} aria-busy={loading} className="overflow-hidden rounded-lg border border-border bg-card text-card-foreground">
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-border px-3 py-2.5">
        <div>
          <h3 id={headingId} className="flex items-center gap-2 text-sm font-medium">
            <Mail className="h-4 w-4 text-primary" aria-hidden="true" /> Email file overview
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">Email entries only. Files in the share stay unchanged.</p>
        </div>
        {items.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {included.length > 0 && <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onExcludedIdsChange([...new Set([...excludedIds, ...items.map(item => item.id)])])}>Exclude all</Button>}
            {removedCount > 0 && <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
              const currentIds = new Set(items.map(item => item.id));
              onExcludedIdsChange(excludedIds.filter(id => !currentIds.has(id)));
            }}>Restore all</Button>}
          </div>
        )}
      </header>

      {error && <div role="alert" className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2 text-xs">
        <span>Could not refresh the email overview.{overview ? " Last loaded entries are shown." : ""}</span>
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={onRetry} disabled={loading}><RotateCcw aria-hidden="true" /> Retry</Button>
      </div>}

      {loading && !overview ? (
        <div role="status" className="space-y-3 p-3">
          <span className="sr-only">Loading email file overview</span>
          {[0, 1].map(key => <div key={key} aria-hidden="true" className="flex gap-3 motion-safe:animate-pulse">
            <div className="h-11 w-14 rounded bg-muted" />
            <div className="flex-1 space-y-2 py-1"><div className="h-3 w-2/3 rounded bg-muted" /><div className="h-2 w-1/3 rounded bg-muted" /></div>
          </div>)}
        </div>
      ) : overview && items.length === 0 ? (
        <div className="flex items-center gap-3 p-4 text-xs text-muted-foreground"><FileText className="h-5 w-5 shrink-0" aria-hidden="true" /><p>No file entries to include. The share link is unchanged.</p></div>
      ) : !overview && !error ? (
        <p className="p-3 text-xs text-muted-foreground">The file overview will appear here when available.</p>
      ) : null}

      {items.length > 0 && <>
        <div role="status" className="flex flex-wrap justify-between gap-1 px-3 py-2 text-xs text-muted-foreground">
          <span>{included.length} of {items.length} entries included{removedCount > 0 ? ` · ${removedCount} removed from email` : ""}</span>
          {loading && <span>Refreshing…</span>}
        </div>
        {included.length === 0 && <p className="mx-3 mb-2 rounded border border-dashed border-border p-3 text-xs text-muted-foreground">No file overview will be added to this email. Restore an entry below to include it again.</p>}
        <ul aria-label="Email file entries" className="max-h-64 overflow-y-auto border-t border-border">
          {items.map(item => {
            const removed = excluded.has(item.id);
            return <li key={item.id} className={`flex items-center gap-3 border-b border-border px-3 py-2 last:border-b-0 ${removed ? "bg-muted/30" : ""}`}>
              <Thumbnail key={`${item.id}:${item.thumbnailDataUrl}:${mode}:${removed}`} src={!removed && mode === "wait" && item.thumbnailStatus === "ready" ? item.thumbnailDataUrl : undefined} />
              <div className="min-w-0 flex-1">
                <p className={`break-words text-xs font-medium ${removed ? "text-muted-foreground line-through" : ""}`}>{item.title || item.fileName}</p>
                {item.title && <p className="break-all text-xs text-muted-foreground">{item.fileName}</p>}
                <p className="mt-0.5 text-[11px] text-muted-foreground">{formatSize(item.sizeBytes)} · {removed ? "Removed from email only" : thumbnailLabel(item, mode)}</p>
              </div>
              <Button type="button" variant="ghost" size="sm" className="h-8 shrink-0 px-2 text-xs" aria-label={`${removed ? "Restore" : "Remove"} ${item.fileName} ${removed ? "to" : "from"} email`} onClick={() => toggle(item.id)}>
                {removed ? <RotateCcw aria-hidden="true" /> : <X aria-hidden="true" />}
                <span className="hidden sm:inline">{removed ? "Restore" : "Remove"}</span>
              </Button>
            </li>;
          })}
        </ul>
        <footer className="space-y-2 border-t border-border bg-muted/20 px-3 py-2.5">
          {(pendingCount > 0 || mode === "without") && <fieldset>
            <legend className="mb-1.5 text-xs font-medium">{pendingCount > 0 ? `${pendingCount} thumbnail${pendingCount === 1 ? "" : "s"} still preparing` : "Thumbnail preference"}</legend>
            <div className="flex flex-col gap-1.5 text-xs sm:flex-row sm:gap-4">
              <label className="flex cursor-pointer items-center gap-2"><input type="radio" name={`${headingId}-mode`} checked={mode === "wait"} onChange={() => onModeChange("wait")} className="accent-primary" /> Wait while I compose</label>
              <label className="flex cursor-pointer items-center gap-2"><input type="radio" name={`${headingId}-mode`} checked={mode === "without"} onChange={() => onModeChange("without")} className="accent-primary" /> Without thumbnails</label>
            </div>
          </fieldset>}
          <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
            <ImageOff className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            <span>Thumbnail cap: {overview?.maxThumbnailCount} images · {formatSize(overview?.maxThumbnailBytes ?? 0)} maximum. Unavailable or over-limit thumbnails stay text-only. Original files are not attached.</span>
          </p>
        </footer>
      </>}
    </section>
  );
}
